import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import useDomain from '@/hooks/useDomain';
import { useClusterFromTaskMutation } from '@/app/KeywordResearch/api/massiveApi';
import { useClusterPlannerFromTaskMutation } from '@/app/KeywordResearch/api/keywordPlannerApi';
import { keywordResearchTooltips } from '@/app/KeywordResearch/tooltip/keywordResearchTooltips';
import {
  hasEnoughKeywordsForCluster,
  isClusterFromTaskDisabled,
  submitClusterFromTask,
  type ClusterFromTaskBody
} from '@/app/KeywordResearch/utils/clusterFromTask';

type ClusterFromTaskMenuItemProps = {
  source: 'massive' | 'planner';
  taskId?: string;
  keywordCount?: number;
  isTaskReady?: boolean;
};

const ClusterFromTaskMenuItem = ({
  source,
  taskId,
  keywordCount = 0,
  isTaskReady = true
}: ClusterFromTaskMenuItemProps) => {
  const { domain_slug, domain } = useDomain();
  const navigate = useNavigate();
  const [clusterMassive, { isLoading: isMassiveClustering }] =
    useClusterFromTaskMutation();
  const [clusterPlanner, { isLoading: isPlannerClustering }] =
    useClusterPlannerFromTaskMutation();

  const mutate = (body: ClusterFromTaskBody) =>
    source === 'massive' ? clusterMassive(body) : clusterPlanner(body);
  const isClustering =
    source === 'massive' ? isMassiveClustering : isPlannerClustering;
  const hasEnoughKeywords = hasEnoughKeywordsForCluster(keywordCount);
  const disabled = isClusterFromTaskDisabled({
    isClustering,
    taskId,
    keywordCount,
    isTaskReady
  });

  const tooltipContent = !hasEnoughKeywords
    ? keywordResearchTooltips.clustering.clusterFromTaskMinKeywords
    : !isTaskReady
      ? keywordResearchTooltips.clustering.clusterFromTaskNotReady
      : keywordResearchTooltips.clustering.clusterFromTask;

  const handleClick = () => {
    if (disabled) return;
    void submitClusterFromTask({
      mutate,
      taskId,
      domainSlug: domain_slug ?? undefined,
      domain: domain ?? undefined,
      navigate,
      hasEnoughKeywords
    });
  };

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`cluster-from-task-menu-${taskId ?? 'unknown'}`}>
          {tooltipContent}
        </Tooltip>
      }
    >
      <span className="d-block">
        <Dropdown.Item
          onClick={handleClick}
          disabled={disabled}
          style={disabled ? { pointerEvents: 'none' } : undefined}
          className={classNames(
            'link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1'
          )}
        >
          <FontAwesomeIcon icon="project-diagram" className="me-1 ms-2" />
          {isClustering ? 'در حال ارسال...' : 'خوشه بندی کن'}
        </Dropdown.Item>
      </span>
    </OverlayTrigger>
  );
};

export default ClusterFromTaskMenuItem;
